import { Info } from "lucide-react";
import PropTypes from "prop-types";

// Crawl-confidence meter: a 3-segment bar (Low / Medium / High) that fills
// cumulatively up to the active level and is color-coded by it. Replaces the
// old "AI crawlers reached n of m pages" copy with a glanceable signal derived
// from the audit's `audit_confidence` (coverage + tooling). Renders nothing for
// an unknown/empty level (legacy docs that predate confidence tracking).
const LEVELS = ["low", "medium", "high"];

const META = {
  low: { label: "Low", fill: "bg-red-500", text: "text-red-400" },
  medium: { label: "Medium", fill: "bg-amber-500", text: "text-amber-400" },
  high: { label: "High", fill: "bg-green-500", text: "text-green-400" },
};

const Tooltip = () => (
  <div
    role="tooltip"
    className="absolute bottom-full left-0 mb-2 z-50 w-64 rounded-lg bg-gray-900 p-3 text-xs text-white shadow-xl opacity-0 pointer-events-none transition-opacity duration-150 group-hover:opacity-100"
  >
    <p className="font-semibold mb-1">Crawl confidence</p>
    <p className="text-gray-300 mb-2">
      Reflects how well our crawler could access and read your website&apos;s content.
    </p>
    <ul className="space-y-1 text-gray-300">
      <li>
        <span className="font-semibold text-green-400">High</span> — We successfully crawled most of
        your site&apos;s pages (≥ 60% coverage).
      </li>
      <li>
        <span className="font-semibold text-amber-400">Medium</span> — We reached 30–60% of pages,
        or some content required JavaScript rendering or was in an unsupported language.
      </li>
      <li>
        <span className="font-semibold text-red-400">Low</span> — Our crawler could only access a
        small fraction of your site (&lt; 30% of pages).
      </li>
    </ul>
    <p className="mt-2 text-gray-400 italic">
      A lower confidence means the audit may not capture your full site.
    </p>
  </div>
);

const ConfidenceMeter = ({ level, className = "" }) => {
  const key = (level || "").toLowerCase();
  const meta = META[key];
  if (!meta) return null;
  const activeIndex = LEVELS.indexOf(key);
  return (
    <div className={`relative group inline-flex items-center gap-2 ${className}`}>
      <Tooltip />
      <span className="text-xs text-on-surface-variant cursor-default">
        Crawl confidence
        <Info
          className="inline-block w-3 h-3 ml-0.5 text-on-surface-variant opacity-60"
          aria-hidden="true"
        />
      </span>
      <meter
        className="flex items-center gap-1"
        aria-label="Crawl confidence"
        aria-valuetext={meta.label}
      >
        {LEVELS.map((lvl, i) => (
          <span
            key={lvl}
            className={`h-1.5 w-6 rounded-full ${
              i <= activeIndex ? meta.fill : "bg-surface-container-high"
            }`}
          />
        ))}
      </meter>
      <span className={`text-xs font-semibold ${meta.text}`}>{meta.label}</span>
    </div>
  );
};

ConfidenceMeter.propTypes = {
  level: PropTypes.oneOf(["low", "medium", "high"]),
  className: PropTypes.string,
};

export default ConfidenceMeter;
